import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {Box, FormControl, MenuItem, Select, Stack, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import type {AggregationSurveyEvaluation} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {formatDateTime, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import React, {useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {InsightsCard} from "../../insights/kit/InsightsCard";
import {NEGATIVE_COLOR, NEUTRAL_COLOR, POSITIVE_COLOR} from "../../insights/kit/insightsColors";
import {directionColor, signed, useInsightsFormatters} from "../../insights/kit/insightsFormat";
import {InsightsLegend} from "../../insights/kit/InsightsLegend";
import {matchesTitle} from "../../insights/kit/InsightsRailSearch";
import {InsightsTooltip, TooltipRow, TooltipTitle} from "../../insights/kit/InsightsTooltip";
import {BAR_DRAW_DURATION, FLOATING_SHADOW} from "../insights/constants";
import {SurveyAnswerContext} from "../shared/surveyAnswer.util";
import {SurveyRequiredMarker} from "../shared/SurveyQuestionMarkers";
import {SurveyEvaluationScopeContext} from "../shared/useSurveyEvaluation";
import {SummaryRow, SummaryRowMuted, summaryRows} from "./questionSummary.util";
import SurveyEvaluationAnswersDialog from "./SurveyEvaluationAnswersDialog";

interface Props {
    survey: Survey
    evaluation?: AggregationSurveyEvaluation
    ctx: SurveyAnswerContext
    /** The date control's range and compare range, named in the card so a trend is never read as a comparison. */
    range: DateRange
    compareRange: DateRange | null
    /** The same question-title filter used by the detailed charts. */
    query?: string
}

/** As the date control writes a range: "08.09.2026 – 07.10.2026". */
const formatRange = ({start, end}: DateRange) => `${formatFullDMY(start)} – ${formatFullDMY(end)}`;

/** As the Insights charts write a day: "09. Sep". */
const formatDay = (date: Date) => formatDateTime('dd. LLL', date);

// The track shares the room left beside the numbers with the question's title, up to a width a dot still reads at.
const TRACK_SX = {flex: '1 1 0', minWidth: {xs: 96, sm: 160}, maxWidth: 640} as const;
const Q_WIDTH = 36;
const NUMBER_WIDTH = 40;
const ROW_PADDING_Y = 6;

/** Dot diameters, from every guest answering alike to the widest split a 0–100 answer can have (a deviation of 50). */
const DOT_MIN = 8;
const DOT_MAX = 24;
const SPREAD_MAX = 50;

/** The scale's reach either side of the centre line: the first that holds the largest move, never under 10. */
const REACHES = [10, 20, 25, 50, 100];

type SortKey = 'survey' | 'lowest' | 'worse';

const SORT_KEYS: SortKey[] = ['survey', 'lowest', 'worse'];

// Rows without a score close every order but the survey's own.
const mutedLast = (left: SummaryRow, right: SummaryRow) => Number(left.muted != null) - Number(right.muted != null);

const SORTERS: Record<SortKey, (left: SummaryRow, right: SummaryRow) => number> = {
    survey: (left, right) => left.number - right.number,
    lowest: (left, right) => mutedLast(left, right) || (left.score ?? 0) - (right.score ?? 0) || left.number - right.number,
    worse: (left, right) =>
        mutedLast(left, right)
        || Number(left.trend == null) - Number(right.trend == null)
        || (left.trend ?? 0) - (right.trend ?? 0)
        || left.number - right.number,
};

const dotSize = (spread: number | null) =>
    DOT_MIN + (Math.min(Math.max(spread ?? 0, 0), SPREAD_MAX) / SPREAD_MAX) * (DOT_MAX - DOT_MIN);

/** Where a move sits on the track, in percent; the centre line is "no change". */
const positionOf = (trend: number, reach: number) => 50 + (Math.max(-reach, Math.min(reach, trend)) / reach) * 50;

const dotColor = (direction: number) => (direction > 0 ? POSITIVE_COLOR : direction < 0 ? NEGATIVE_COLOR : NEUTRAL_COLOR);

const motion = `left ${BAR_DRAW_DURATION}ms ease-out, width ${BAR_DRAW_DURATION}ms ease-out, height ${BAR_DRAW_DURATION}ms ease-out`;

interface DotProps {
    position: number
    size: number
    fill: string
    border?: string
}

/** Shown where it belongs on load, then slid and resized when the period changes - like the bars on this tab. */
function Dot({position, size, fill, border}: DotProps) {
    return (
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: `${position}%`,
                width: size,
                height: size,
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                bgcolor: fill,
                border,
                boxSizing: 'border-box',
                transition: motion,
            }}
        />
    );
}

interface RowLabels {
    score: string
    trend: string
    spread: string
    answers: string
    fromComments: string
    muted: Record<SummaryRowMuted, string>
}

interface SummaryRowViewProps {
    row: SummaryRow
    reach: number
    hasCompare: boolean
    compareColor: string
    labels: RowLabels
    formatWhole: (value: number) => string
    onOpen?: () => void
}

function SummaryRowView({row, reach, hasCompare, compareColor, labels, formatWhole, onOpen}: SummaryRowViewProps) {
    const theme = useTheme();

    const change = row.trend == null ? null : signed(row.trend, formatWhole);
    const before = hasCompare && row.compareTrend != null ? row.compareTrend : null;
    const now = row.trend == null ? null : positionOf(row.trend, reach);
    const then = before == null ? null : positionOf(before, reach);

    const trendText = (trend: number | null) => (trend == null ? '—' : signed(trend, formatWhole).text);
    const numberText = (value: number | null) => (value == null ? '—' : formatWhole(value));

    // "80 → 76" while comparing: where the question stood in the compare period, and where it stands now.
    const moved = (earlier: string, current: string) => (hasCompare ? `${earlier} → ${current}` : current);

    const panel = row.score == null ? '' : (
        <React.Fragment>
            <TooltipTitle title={`Q${row.number}`} subtitle={row.title}/>
            <TooltipRow label={labels.score} value={moved(numberText(row.compareScore), numberText(row.score))}/>
            <TooltipRow
                swatch={change == null ? undefined : dotColor(change.direction)}
                label={labels.trend}
                value={moved(trendText(row.compareTrend), trendText(row.trend))}
            />
            {/* Where the trend comes from: the line through the answers, from the first one in the range to the last. */}
            {row.trendStart != null && row.trendEnd != null && (
                <TooltipRow
                    label={`${formatDay(row.trendStart.at)} → ${formatDay(row.trendEnd.at)}`}
                    value={`${formatWhole(row.trendStart.score)} → ${formatWhole(row.trendEnd.score)}`}
                />
            )}
            <TooltipRow label={labels.spread} value={moved(numberText(row.compareSpread), numberText(row.spread))}/>
            <TooltipRow label={labels.answers} value={formatWhole(row.answers)}/>
        </React.Fragment>
    );

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            {...(onOpen && {
                role: 'button',
                tabIndex: 0,
                'aria-label': `${labels.answers}: Q${row.number}. ${row.title}`,
                'aria-haspopup': 'dialog' as const,
                onClick: onOpen,
                onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onOpen();
                },
            })}
            sx={{
                // On a phone the question takes its own line, with the score, the dots and the trend under it.
                flexWrap: {xs: 'wrap', sm: 'nowrap'},
                columnGap: 1.5,
                rowGap: 0.5,
                minWidth: 0,
                px: 1,
                mx: -1,
                py: `${ROW_PADDING_Y}px`,
                borderRadius: 1.5,
                cursor: onOpen ? 'pointer' : 'default',
                transition: 'background-color 120ms ease',
                '&:hover': {backgroundColor: alpha(theme.palette.text.primary, 0.03)},
                '&:focus-visible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2},
            }}
        >
            <Typography sx={{width: Q_WIDTH, flexShrink: 0, fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums'}}>
                {`Q${row.number}`}
            </Typography>

            <Stack direction={'row'} alignItems={'center'} gap={0.75} sx={{flex: {xs: `1 1 calc(100% - ${Q_WIDTH + 12}px)`, sm: '1 1 0'}, minWidth: 0}}>
                <Typography noWrap title={row.title} sx={{minWidth: 0, fontSize: 14, color: row.muted ? 'text.secondary' : 'text.primary'}}>
                    {row.title}
                </Typography>

                {row.isRequired && <SurveyRequiredMarker size={14}/>}

                {row.fromComments && (
                    <AutoAwesomeIcon titleAccess={labels.fromComments} sx={{fontSize: 14, color: 'text.secondary', flexShrink: 0}}/>
                )}

                {row.muted && (
                    <Typography noWrap sx={{flexShrink: 0, fontSize: 13, color: 'text.disabled'}}>
                        {labels.muted[row.muted]}
                    </Typography>
                )}
            </Stack>

            <Typography
                sx={{width: NUMBER_WIDTH, flexShrink: 0, textAlign: 'right', fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}
            >
                {row.score == null ? '' : formatWhole(row.score)}
            </Typography>

            <InsightsTooltip followCursor placement={'top'} title={panel}>
                <Box sx={{position: 'relative', height: DOT_MAX, ...TRACK_SX}}>
                    {/* Through the row's padding too, so the rows draw one unbroken centre line. */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -ROW_PADDING_Y,
                            bottom: -ROW_PADDING_Y,
                            left: '50%',
                            width: '2px',
                            ml: '-1px',
                            bgcolor: alpha(theme.palette.text.primary, 0.3),
                        }}
                    />

                    {then != null && now != null && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: `${Math.min(then, now)}%`,
                                width: `${Math.abs(now - then)}%`,
                                height: '1.5px',
                                transform: 'translateY(-50%)',
                                bgcolor: compareColor,
                                transition: motion,
                            }}
                        />
                    )}

                    {then != null && (
                        <Dot
                            position={then}
                            size={dotSize(row.compareSpread)}
                            fill={theme.palette.background.paper}
                            border={`1.5px solid ${compareColor}`}
                        />
                    )}

                    {now != null && change != null && <Dot position={now} size={dotSize(row.spread)} fill={dotColor(change.direction)}/>}
                </Box>
            </InsightsTooltip>

            <Typography
                sx={{
                    width: NUMBER_WIDTH,
                    flexShrink: 0,
                    textAlign: 'right',
                    fontSize: 14,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: change == null ? 'text.disabled' : directionColor(change.direction),
                }}
            >
                {row.muted ? '' : change?.text ?? '—'}
            </Typography>
        </Stack>
    );
}

/** A legend entry with a mark of its own - the kit's legend has no dot or icon shape. */
function MarkEntry({label, mark}: {label: string; mark: React.ReactNode}) {
    return (
        <Stack direction={'row'} alignItems={'center'} spacing={0.75} sx={{minWidth: 0}}>
            {mark}
            <Typography sx={{color: 'text.secondary', fontSize: 14, fontWeight: 600, lineHeight: '20px'}} noWrap>
                {label}
            </Typography>
        </Stack>
    );
}

const legendDot = (size: number, fill: string, border?: string) => (
    <Box sx={{width: size, height: size, borderRadius: '50%', bgcolor: fill, border, boxSizing: 'border-box', flexShrink: 0}}/>
);

/**
 * Every question a guest answers, one row each: its score, how it moved over the selected dates (left of the centre
 * line it got worse, right of it better) and, by the dot's size, how far apart the guests' answers lie. Free-text
 * questions are scored from the AI's reading of their comments; a question without a score stays, muted, with the
 * reason. A compare period set in the date control adds a hollow dot for how the question moved back then.
 */
export default function SurveyQuestionSummaryCard({survey, evaluation, ctx, range, compareRange, query = ''}: Props) {
    const {t} = useTranslation();
    const theme = useTheme();
    const {whole: formatWhole} = useInsightsFormatters();
    const scope = useContext(SurveyEvaluationScopeContext);

    const [sort, setSort] = useState<SortKey>('lowest');
    const [listedQuestionId, setListedQuestionId] = useState<string>();

    const hasCompare = evaluation?.compareResponseCount != null;
    const compareColor = alpha(theme.palette.text.primary, 0.45);

    const allRows = useMemo(() => summaryRows(survey, evaluation, ctx).sort(SORTERS[sort]), [ctx, evaluation, sort, survey]);
    const rows = useMemo(() => allRows.filter(row => matchesTitle(query, row.title)), [allRows, query]);
    const listedQuestion = survey.questions?.find(question => question.id === listedQuestionId);
    const listedRow = allRows.find(row => row.id === listedQuestionId);

    const reach = useMemo(() => {
        // Filtering titles must not change where a question's dot sits on the scale.
        const largest = allRows.reduce(
            (max, row) => Math.max(max, Math.abs(row.trend ?? 0), hasCompare ? Math.abs(row.compareTrend ?? 0) : 0),
            0
        );
        return REACHES.find(candidate => candidate >= largest) ?? REACHES[REACHES.length - 1];
    }, [hasCompare, allRows]);

    // Nothing in the survey can be scored - a pre-arrival form, say: the card has nothing to sum up.
    if (!allRows.some(row => row.score != null) || !rows.length) return null;

    const labels: RowLabels = {
        score: t('Score'),
        trend: t('Trend'),
        spread: t('Spread'),
        answers: t('Answers'),
        fromComments: t('Read from comments'),
        muted: {
            sensitive: t('Sensitive'),
            noAnswers: t('No answers'),
            notRead: t('Not read yet'),
            noScore: t('No score'),
        },
    };

    const sortLabels: Record<SortKey, string> = {
        survey: t('Survey order'),
        lowest: t('Lowest score'),
        worse: t('Getting worse first'),
    };

    const anyBefore = hasCompare && rows.some(row => row.compareTrend != null);
    const anyFromComments = rows.some(row => row.fromComments);

    // In the header from sm up. A phone's header has no room left beside the title, so there it sits above the rows.
    const sortPicker = (placement: 'header' | 'toolbar') => (
        <FormControl
            size={'small'}
            fullWidth={placement === 'toolbar'}
            sx={placement === 'header'
                ? {display: {xs: 'none', sm: 'inline-flex'}, minWidth: 180, maxWidth: 280}
                : {display: {xs: 'flex', sm: 'none'}, mb: 2}}
        >
            <Select<SortKey>
                value={sort}
                onChange={event => setSort(event.target.value as SortKey)}
                renderValue={value => `${t('Sort by')}: ${sortLabels[value]}`}
                inputProps={{'aria-label': t('Sort by')}}
                sx={{height: 36, '& .MuiSelect-select': {py: 0.75}}}
                MenuProps={{PaperProps: {variant: 'outlined', sx: {boxShadow: FLOATING_SHADOW}}}}
            >
                {SORT_KEYS.map(key => (
                    <MenuItem key={key} value={key}>
                        {sortLabels[key]}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const columnHeader = (text: string) => (
        <Typography sx={{width: NUMBER_WIDTH, flexShrink: 0, textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'text.secondary'}}>
            {text}
        </Typography>
    );

    return (
        <InsightsCard
            title={t('Questions at a glance')}
            hint={t('The number is each question\'s score from 0 to 100. The dot shows how it moved over the selected dates - left it got worse, right it got better - and the bigger the dot, the more the guests disagree. Free-text questions are scored from how the AI read their comments; sensitive answers are never sent to the AI. With a compare period, a hollow dot shows how the question moved back then.')}
            headline={(
                <Typography sx={{fontSize: 13, color: 'text.secondary'}}>
                    {compareRange == null
                        ? t('Trend within {{period}}', {period: formatRange(range)})
                        : t('Trend within {{period}}, compared with {{compare}}', {period: formatRange(range), compare: formatRange(compareRange)})}
                </Typography>
            )}
            actions={sortPicker('header')}
            footer={(
                <InsightsLegend>
                    <MarkEntry label={t('Guests disagree')} mark={legendDot(DOT_MAX - 6, NEUTRAL_COLOR)}/>
                    <MarkEntry label={t('Guests agree')} mark={legendDot(DOT_MIN, NEUTRAL_COLOR)}/>
                    {anyBefore && (
                        <MarkEntry label={t('Compare period')} mark={legendDot(10, 'transparent', `1.5px solid ${compareColor}`)}/>
                    )}
                    {anyFromComments && (
                        <MarkEntry label={t('Read from comments')} mark={<AutoAwesomeIcon sx={{fontSize: 14, color: 'text.secondary'}}/>}/>
                    )}
                </InsightsLegend>
            )}
        >
            {sortPicker('toolbar')}

            <Stack direction={'row'} alignItems={'flex-end'} gap={1.5} sx={{mb: 0.5}}>
                {/* On a phone the rows put the numbers on their own line, so the header starts where they do. */}
                <Box sx={{width: Q_WIDTH, flexShrink: 0, display: {xs: 'none', sm: 'block'}}}/>
                <Box sx={{flex: 1, minWidth: 0, display: {xs: 'none', sm: 'block'}}}/>
                {columnHeader(t('Score'))}
                <Stack direction={'row'} justifyContent={'space-between'} sx={TRACK_SX}>
                    <Typography sx={{fontSize: 12, fontWeight: 700, color: NEGATIVE_COLOR}}>{`← ${t('Getting worse')}`}</Typography>
                    <Typography sx={{fontSize: 12, fontWeight: 700, color: POSITIVE_COLOR}}>{`${t('Getting better')} →`}</Typography>
                </Stack>
                {columnHeader(t('Trend'))}
            </Stack>

            <Stack>
                {rows.map(row => (
                    <SummaryRowView
                        key={row.id}
                        row={row}
                        reach={reach}
                        hasCompare={hasCompare}
                        compareColor={compareColor}
                        labels={labels}
                        formatWhole={formatWhole}
                        onOpen={scope?.surveyId && (evaluation?.questions.find(question => question.questionId === row.id)?.answeredCount ?? 0) > 0
                            ? () => setListedQuestionId(row.id)
                            : undefined}
                    />
                ))}
            </Stack>
            {listedQuestion && listedRow && (
                <SurveyEvaluationAnswersDialog
                    key={listedQuestionId}
                    question={listedQuestion}
                    number={listedRow.number}
                    summary={{score: listedRow.score, trend: listedRow.trend}}
                    ctx={ctx}
                    includeSensitive={false}
                    onClose={() => setListedQuestionId(undefined)}
                />
            )}
        </InsightsCard>
    );
}
